import { Component, type ErrorInfo, type ReactNode } from 'react'
import * as Sentry from '@sentry/react'
import { Warning, ArrowsClockwise } from '@phosphor-icons/react'
import { captureException } from '@/lib/posthog'
import { Button } from './Button'

interface ErrorBoundaryProps {
  children: ReactNode
  /** Optional fallback UI. If omitted, the default error card is shown. */
  fallback?: ReactNode
  /** Called when an error is caught — useful for logging to an error service. */
  onError?: (error: Error, errorInfo: ErrorInfo) => void
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

/**
 * Catches JavaScript errors in child components and renders a fallback UI
 * instead of crashing the entire application.
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('[ErrorBoundary]', error, errorInfo)
    // Report the render error to Sentry with the React component stack.
    // No-op when Sentry isn't initialised (dev / no DSN).
    Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo.componentStack } },
    })
    // Also report to PostHog Error Tracking. A render error caught here never
    // reaches window.onerror, so exception autocapture cannot see it — without
    // this the issue would be invisible in PostHog even though the user hit a
    // broken screen. No-op when PostHog isn't initialised (dev / no key).
    captureException(error, {
      react_component_stack: errorInfo.componentStack,
      error_boundary: true,
    })
    this.props.onError?.(error, errorInfo)
  }

  private handleReset = (): void => {
    this.setState({ hasError: false, error: null })
  }

  private handleReload = (): void => {
    window.location.reload()
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className="flex items-center justify-center min-h-[300px] p-6">
          <div className="text-center max-w-md">
            <div className="h-14 w-14 rounded-2xl bg-red-500/10 flex items-center justify-center mx-auto mb-4">
              <Warning className="h-6 w-6 text-red-500" />
            </div>
            <h2 className="text-lg font-emphasis tracking-tight text-foreground mb-2">
              Something went wrong
            </h2>
            <p className="text-sm text-muted-foreground mb-6">
              An unexpected error occurred. You can try again or reload the page.
            </p>
            {this.state.error && (
              <pre className="text-xs text-left bg-muted rounded-lg p-3 mb-6 overflow-auto max-h-32 text-muted-foreground">
                {this.state.error.message}
              </pre>
            )}
            <div className="flex items-center justify-center gap-3">
              <Button variant="outline" onClick={this.handleReset}>
                <ArrowsClockwise className="h-4 w-4 mr-2" />
                Try Again
              </Button>
              <Button variant="default" onClick={this.handleReload}>
                Reload Page
              </Button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export { ErrorBoundary }
